import { useRef, useState, useEffect, useContext } from "react";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import ProfileInfo from "layouts/pages/home/components/Settings/components/ProfileInfo";
import ProfileDetailsRows from "layouts/pages/settings/components/ProfileDetailsRows";
import validations from "layouts/pages/home/components/Settings/schemas/validations";
import form from "layouts/pages/home/components/Settings/schemas/form";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth0 } from "@auth0/auth0-react";
import { updateQuery } from "api/UdasWrapper";
import { UserContext } from "context/AppProvider/UserContext";
import Header from "layouts/pages/settings/components/Header";
import { useDispatch } from "react-redux";
import { useRoles } from "hooks/useRoles";
import { UPDATE_USER } from "redux/constants/portal";
import { getCookie } from "helpers/common";
import ChangePassword from "layouts/Security/components/ChangePassword";
import ShowAccessToken from "layouts/Security/components/ShowAccessToken";
import TwoStepAuthentication from "layouts/Security/components/TwoStepAuthentication";

function SectionCard({ title, children, action }) {
  return (
    <Card
      sx={{
        height: "auto",
        boxShadow: "none !important",
        borderRadius: "8px !important",
        background: "var(--portal-surface-bg) !important",
        border: "1px solid var(--portal-surface-border) !important"
      }}
    >
      <SoftBox p={{ xs: 2.5, sm: 3 }} display="flex" alignItems="center" justifyContent="space-between">
        <SoftTypography variant="h5" fontWeight="bold" className="white-to-dark">
          {title}
        </SoftTypography>

        {action ? <SoftBox>{action}</SoftBox> : null}
      </SoftBox>

      <SoftBox px={{ xs: 2.5, sm: 3 }} pb={{ xs: 2.5, sm: 3 }}>{children}</SoftBox>
    </Card>
  );
}

function Settings({ getUser }) {
  const { userData, setUserData } = useContext(UserContext);
  const [roleOptions, setRoleOptions] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const { formId, formField } = form;
  const { user } = useAuth0();
  const { roles } = useRoles(false);
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const handleSubmit = async (values) => {
    const input = {
      email_address: `${values.email_address}`,
      first_name: `${values.first_name}`,
      mobile: `${values.mobile}`,
      last_name: `${values.last_name}`,
      organization: `${values.organization}`,
      phone: `${values.phone}`,
      role_id: `${values.role_id}`,
      user_type: "tenant",
      username: `${values.email_address}`,
      image: `${userData?.image || ""}`,
      your_location: `${values.your_location}`,
      language: `${values.language}`,
      status: `${values.status}`
    };

    const data = JSON.stringify({
      query: `mutation UpdateUser($tenantId: String, $auth0Id: String, $input: UserUpdateInput) {
        updateUser(tenant_id: $tenantId, auth0_id: $auth0Id, input: $input) {
          isSuccessful
          message
          resultObject
        }
      }`,
      variables: {
        tenantId: `${user.stripeId}`,
        auth0Id: `${values.auth0_id}`,
        input
      }
    });

    const reduxInput = {
      ...input,
      auth0_id: values.auth0_id,
      tenant_id: values.tenant_id
    };

    try {
      await updateQuery(data);
      setUserData({ ...userData, ...reduxInput });
      getUser();
      toast.success("User Profile Updated successfully!");
      dispatch({
        type: UPDATE_USER,
        payload: reduxInput
      });
      setIsEditing(false);
      formRef.current.resetForm({
        values: {
          id: values.id,
          auth0_id: values.auth0_id,
          tenant_id: values.tenant_id,
          role_id: values.role_id,
          username: values.username,
          user_type: values.user_type,
          status: values.status,
          first_name: values.first_name,
          last_name: values.last_name,
          organization: values.organization,
          your_location: values.your_location,
          mobile: values.mobile,
          email_address: values.email_address,
          phone: values.phone,
          language: values.language
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const submitForm = (event) => {
    event.preventDefault();
    formRef?.current?.submitForm();
  };

  const handleCancel = () => {
    formRef.current?.resetForm();
    setIsEditing(false);
  };

  const isDataLoaded = userData && roles && roleOptions;

  useEffect(() => {
    if (!Array.isArray(roles) || !userData) return;
    const ownerRoleId = roles.find((role) => role.name?.toLowerCase() === "owner")?.id;
    const currentUserIsOwner = Boolean(ownerRoleId && userData.role_id === ownerRoleId);
    setIsOwner(currentUserIsOwner);
    setRoleOptions(
      roles
        .filter((role) => currentUserIsOwner || role.name?.toLowerCase() !== "owner")
        .map((role) => ({ value: role.id, label: role.name }))
    );
  }, [roles, userData]);

  useEffect(() => {
    if (roles && userData) {
      setUserRole(roles?.find((role) => role.id === userData.role_id)?.name);
    }
  }, [roles, userData]);

	return (
		<>
			<SoftBox sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
				<DashboardNavbar title="Profile Overview" flush />
			</SoftBox>
			<DashboardLayout flush>
				<SoftBox
					sx={{ width: "100%", maxWidth: "1600px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 }, pt: { xs: 3, lg: 4 }, pb: 5 }}
				>
					<Card
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						backgroundColor: "transparent !important",
						boxShadow: "none !important",
						overflow: "hidden",
						mt: 0
					}}
					>
						<Card
							sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden"
							}}
						>
							<SoftBox
								sx={{
									width: "100%",
									margin: "0 auto",
									px: 0,
									pb: 0
								}}
							>
								<Header otherUser={userData} isOwner={isOwner} label="My profile" />
							</SoftBox>

							<SoftBox sx={{ overflow: "visible" }}>
							<SoftBox
								sx={{
								width: "100%",
								margin: "0 auto",
								px: 0,
								py: 0
								}}
							>
								<Grid container spacing={3} alignItems="flex-start">
									<Grid size={{ xs: 12, lg: 8 }}>
										{isDataLoaded && (
										<SectionCard title="Profile details" action={!isEditing && (
                                            <SoftButton variant="outlined" color="primary" size="small" onClick={() => setIsEditing(true)}>
                                              Edit profile
                                            </SoftButton>
                                          )}>
											{isEditing ? <Formik
											initialValues={userData}
											validationSchema={validations}
												onSubmit={handleSubmit}
												enableReinitialize
											innerRef={formRef}
											validateOnBlur
											>
											{({ values, errors, touched, setFieldValue, dirty }) => (
												<Form id={formId} autoComplete="off">
												<ProfileInfo
													formData={{
													values,
													touched,
													formField,
													errors,
													roleOptions,
													setFieldValue,
													userRole
													}}
												/>

												<SoftBox
													display="flex"
													justifyContent="flex-end"
													gap={2}
													mt={3}
												>
													<SoftButton
													variant="outlined"
													color="info"
														onClick={handleCancel}
													>
													Cancel
													</SoftButton>

													<SoftButton
													onClick={submitForm}
													variant="gradient"
													color="info"
													disabled={!dirty}
													>
													Save Changes
													</SoftButton>
												</SoftBox>
												</Form>
											)}
											</Formik> : <ProfileDetailsRows user={userData} roleName={userRole} />}
										</SectionCard>
										)}
									</Grid>
									<Grid size={{ xs: 12, lg: 4 }} className="profile-security-cards">
										<SectionCard title="Account security">
											<SoftBox display="flex" flexDirection="column" gap={2}>
												<ShowAccessToken token={getCookie("accessToken")} />
												<TwoStepAuthentication />
												{user?.sub?.startsWith("auth0") && <ChangePassword />}
											</SoftBox>
										</SectionCard>
									</Grid>
								</Grid>
							</SoftBox>
							</SoftBox>
						</Card>
					</Card>

				</SoftBox>
			</DashboardLayout>
		</>
	);
}

export default Settings;
